const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (e) {
    console.error('SW registration failed:', e);
    return null;
  }
}

/** Does NOT wait indefinitely — uses getRegistration instead of .ready */
export async function getSubscriptionStatus() {
  if (!isPushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

export async function subscribeToPush(token) {
  if (!isPushSupported()) throw new Error('Push notifications are not supported in this browser.');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission was denied. Please enable it in your browser settings.');

  // Register SW if not already done, or get existing registration
  let reg = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!reg) reg = await navigator.serviceWorker.register('/sw.js');

  // Wait for SW to become active with a 5s safety timeout
  if (!reg.active) {
    await Promise.race([
      new Promise((resolve) => {
        const sw = reg.installing || reg.waiting;
        if (!sw) { resolve(); return; }
        sw.addEventListener('statechange', function handler() {
          if (sw.state === 'activated') { sw.removeEventListener('statechange', handler); resolve(); }
        });
      }),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Service worker not ready. Please refresh and try again.')), 5000)),
    ]);
  }

  const res = await fetch(`${API}/notifications/vapid-public-key`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const { publicKey } = await res.json();

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  await fetch(`${API}/notifications/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ subscription: sub.toJSON() }),
  });

  return sub;
}

export async function unsubscribeFromPush(token) {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await sub.unsubscribe();
    await fetch(`${API}/notifications/unsubscribe`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  }
}

export async function getPreferences(token) {
  const res = await fetch(`${API}/notifications/preferences`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.json();
}

export async function savePreferences(token, prefs) {
  const res = await fetch(`${API}/notifications/preferences`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(prefs),
  });
  return res.json();
}
